import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { LEGEND_ITEMS } from "../../../components/UIToolkit/Coverage/CoverageDeltaCell";
import { CHARGE, COVERAGE, INK, NEUTRAL, OVERTIME, SHIFT_PILL } from "../../../theme/tokens";
import { useIsJobAxis } from "./usePeopleCellLabel";

/** People-axis markers. A worked cell has no fixed swatch — it is filled with its job's (or role's)
    categorical hue and names that job in the cell itself, so only the OFF state is keyable here.
    Day/night is not encoded on this axis at all. */
const PEOPLE_LEGEND: { id: string; bg: string; dot: string; round: boolean; key: string }[] = [
	{ id: "off", bg: SHIFT_PILL.OFF_BG, dot: "", round: true, key: "schedule.template.v3.people.legend.off" },
];

/** The axis/density-specific legend under the toolbar: People shift markers, Coverage-Detail
 * charge/OT/PTO/drag markers, or the Coverage-Summary deficit-tier swatches. */
export const CoverageLegend: FC<{ isPeople: boolean; isCoverageDetail: boolean }> = ({ isPeople, isCoverageDetail }) => {
	const { t } = useTranslation();
	const isJobAxis = useIsJobAxis();

	if (isPeople) {
		return (
			<Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }} data-testid="ms3-people-legend">
				<Typography sx={{ fontSize: 12, fontWeight: 600, color: INK.MUTED }}>{t("schedule.template.v3.people.legend.title")}</Typography>
				{/* Worked cells are colored per job/role, an open-ended set — so the legend states the RULE
				    rather than enumerating swatches it would immediately fall out of date with. */}
				<Typography sx={{ fontSize: 12, color: INK.SECONDARY }} data-testid="ms3-people-legend-color-note">
					{t(isJobAxis ? "schedule.template.v3.people.legend.colorByJob" : "schedule.template.v3.people.legend.colorByRole")}
				</Typography>
				{PEOPLE_LEGEND.map((l) => (
					<Box key={l.id} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
						<Box
							sx={{
								width: 16,
								height: 16,
								borderRadius: 0.75,
								backgroundColor: l.bg,
								border: `1px solid ${NEUTRAL[200]}`,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							{l.dot && <Box sx={{ width: 7, height: 7, borderRadius: l.round ? "50%" : "2px", backgroundColor: l.dot }} />}
						</Box>
						<Typography sx={{ fontSize: 12, color: INK.SECONDARY }}>{t(l.key)}</Typography>
					</Box>
				))}
				{/* OT badges appear in both Summary and Detail, so the OT key does too. */}
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
					<Box sx={{ px: 0.5, borderRadius: 0.75, backgroundColor: OVERTIME.BG, color: OVERTIME.INK, fontSize: 9.5, fontWeight: 800 }}>
						{t("schedule.template.v3.people.otBadge")}
					</Box>
					<Typography sx={{ fontSize: 12, color: INK.SECONDARY }}>{t("schedule.template.v3.people.legend.ot")}</Typography>
				</Box>
			</Box>
		);
	}

	if (isCoverageDetail) {
		return (
			<Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }} data-testid="ms3-detail-legend">
				<Typography sx={{ fontSize: 12, fontWeight: 600, color: INK.MUTED }}>{t("schedule.template.v3.detail.legend.title")}</Typography>
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
					<Box sx={{ px: 0.5, borderRadius: 0.5, backgroundColor: CHARGE.BG, color: CHARGE.INK, fontSize: 9.5, fontWeight: 800 }}>
						{t("schedule.template.v3.detail.charge")}
					</Box>
					<Typography sx={{ fontSize: 12, color: INK.SECONDARY }}>{t("schedule.template.v3.detail.legend.charge")}</Typography>
				</Box>
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
					<Box sx={{ px: 0.5, borderRadius: 0.5, backgroundColor: OVERTIME.BG, color: OVERTIME.INK, fontSize: 9.5, fontWeight: 800 }}>
						{t("schedule.template.v3.people.otBadge")}
					</Box>
					<Typography sx={{ fontSize: 12, color: INK.SECONDARY }}>{t("schedule.template.v3.detail.legend.ot")}</Typography>
				</Box>
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
					<Box
						sx={{
							position: "relative",
							width: 16,
							height: 16,
							borderRadius: 0.75,
							backgroundColor: NEUTRAL[100],
							border: `1px solid ${NEUTRAL[200]}`,
							"&::after": {
								content: '""',
								position: "absolute",
								top: 0,
								right: 0,
								width: 0,
								height: 0,
								borderStyle: "solid",
								borderWidth: "0 7px 7px 0",
								borderColor: `transparent ${COVERAGE.PTO_MARK} transparent transparent`,
							},
						}}
					/>
					<Typography sx={{ fontSize: 12, color: INK.SECONDARY }}>{t("schedule.template.v3.detail.legend.pto")}</Typography>
				</Box>
				<Box sx={{ width: "1px", alignSelf: "stretch", backgroundColor: NEUTRAL[200] }} />
				<Typography sx={{ fontSize: 11.5, color: INK.MUTED }}>{t("schedule.template.v3.detail.dragHint")}</Typography>
			</Box>
		);
	}

	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
			<Typography sx={{ fontSize: 12, fontWeight: 600, color: INK.MUTED }}>{t("schedule.template.v3.legend.title")}</Typography>
			{LEGEND_ITEMS.map((l) => (
				<Box key={l.tier} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
					{/* Swatch mirrors the cell: deficit tiers carry the same left severity bar. */}
					<Box
						sx={{
							position: "relative",
							width: 16,
							height: 16,
							borderRadius: 0.75,
							backgroundColor: l.bg,
							border: `1px solid ${NEUTRAL[200]}`,
							overflow: "hidden",
							...(l.bar
								? {
										"&::before": {
											content: '""',
											position: "absolute",
											top: 0,
											bottom: 0,
											left: 0,
											width: "3px",
											backgroundColor: l.bar,
										},
									}
								: {}),
						}}
					/>
					<Typography sx={{ fontSize: 12, color: INK.SECONDARY }}>{t(l.labelKey)}</Typography>
				</Box>
			))}
		</Box>
	);
};
